import type { ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';

export interface SectionCardProps {
  title?: ReactNode;
  subheader?: ReactNode;
  action?: ReactNode;
  /** Removes the content padding — use for a full-bleed table. */
  disableContentPadding?: boolean;
  /**
   * Fills the parent's height and scrolls its OWN content.
   *
   * For a card inside a height-constrained region — the measured panels CLAUDE.md's
   * "a screen header never scrolls" asks for. The header and its divider stay put and
   * only the body moves, which `height: 100%` alone cannot do: a card is a block, its
   * content grows it, and the page scrolls instead. `minHeight: 0` is what lets the
   * content area shrink below its content, and without it the body refuses and the
   * scroll comes straight back.
   *
   * Off by default, so every existing card behaves exactly as before.
   */
  fill?: boolean;
  children: ReactNode;
}

/** A titled surface. The default container for any block of content on a page. */
export function SectionCard({
  title,
  subheader,
  action,
  disableContentPadding,
  fill,
  children,
}: SectionCardProps) {
  const contentSx: SxProps<Theme> = {
    ...(disableContentPadding ? { p: 0, '&:last-child': { pb: 0 } } : {}),
    ...(fill ? { flex: 1, minHeight: 0, overflowY: 'auto' } : {}),
  };

  return (
    <Card
      sx={
        fill
          ? {
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
            }
          : undefined
      }
    >
      {title ? (
        <>
          <CardHeader
            title={title}
            subheader={subheader}
            action={action}
            sx={fill ? { flexShrink: 0 } : undefined}
          />
          <Divider sx={fill ? { flexShrink: 0 } : undefined} />
        </>
      ) : null}
      <CardContent sx={disableContentPadding || fill ? contentSx : undefined}>
        {children}
      </CardContent>
    </Card>
  );
}
